import { ButtonError } from 'components/Button'
import CurrencyInputPanel from 'components/CurrencyInputPanel'
import { useApproveStep } from 'components/StepLine/common/ApproveStep'
import Step from 'components/StepLine/common/Step'
import useStepWithNext from 'components/StepLine/context/hooks/useStepWithNext'
import StepLineModal from 'components/StepLine/StepLineModal'
import { useConnection } from 'hooks/useConnection'
import { useTokenBalance } from 'lib/hooks/useCurrencyBalance'
import tryParseCurrencyAmount from 'lib/utils/tryParseCurrencyAmount'
import { useOnStake, useOnVaultStake } from 'pages/Islands/IslandPage/IslandManagementCard/hooks/useOnStakeCallback'
import { useCallback, useMemo, useState } from 'react'

import { REFETCH_POLICY_FAST } from '../../../config/RefetchPolicies'
import { SharedProps } from '.'
import { useSelectLockTimeStep } from './steps/SelectLockTimeStep'
// import StakeLocked from './steps/StakeLocked'
import StakeStep from './steps/StakeStep'

const StakeView = ({ token, farm, rewardVault, showMigrationOption, stakingType = 'farm' }: SharedProps) => {
  const [value, setValue] = useState('')
  const { account } = useConnection()
  const balance = useTokenBalance(account, token || undefined, REFETCH_POLICY_FAST)
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false)

  const lockTimeSelectStep = useSelectLockTimeStep(farm, value)

  const typedValue = useMemo(() => tryParseCurrencyAmount(value, token || undefined), [value, token])
  const isError = useMemo(() => typedValue && balance?.lessThan(typedValue), [typedValue])

  const approveStep = useApproveStep(typedValue, stakingType === 'farm' ? farm?.address : rewardVault)
  const {
    onStake: onFarmStake,
    attemptingTxnStake: attemptingTxnFarmStake,
    txHashStake: txHashFarmStake,
    clear: clearFarmStake,
  } = useOnStake(typedValue, stakingType === 'farm' ? farm?.address : undefined)

  // Vault staking callback
  const {
    onStake: onVaultStake,
    attemptingTxnStake: attemptingTxnVaultStake,
    txHashStake: txHashVaultStake,
    clear: clearVaultStake,
  } = useOnVaultStake(typedValue, stakingType === 'vault' ? rewardVault : undefined)

  const onStake = useMemo(() => {
    return stakingType === 'farm' ? onFarmStake : onVaultStake
  }, [stakingType, onFarmStake, onVaultStake])

  const attemptingTxnStake = useMemo(() => {
    return stakingType === 'farm' ? attemptingTxnFarmStake : attemptingTxnVaultStake
  }, [stakingType, attemptingTxnFarmStake, attemptingTxnVaultStake])

  const txHashStake = useMemo(() => {
    return stakingType === 'farm' ? txHashFarmStake : txHashVaultStake
  }, [stakingType, txHashFarmStake, txHashVaultStake])

  const clear = useCallback(() => {
    if (stakingType === 'farm') {
      clearFarmStake()
    } else {
      clearVaultStake()
    }
  }, [stakingType, clearFarmStake, clearVaultStake])

  const onFinish = () => {
    clear()
    setIsConfirmationOpen(false)
    setValue('')
  }

  const stakeStep = useStepWithNext('StakeStep', StakeStep, {
    amount: typedValue,
    onStake,
    hash: txHashStake,
    attemptingTxn: attemptingTxnStake,
    pendingText: (
      <>
        Stake {typedValue?.toSignificant(6)} {typedValue?.currency.symbol}
      </>
    ),
    transactionWaiting: true,
    autoConfirm: true,
  })

  const steps: Step[] = useMemo(() => {
    const baseSteps = [approveStep]

    // Only include lock time selection for farm staking
    if (stakingType === 'farm') {
      baseSteps.push(lockTimeSelectStep)
    }

    baseSteps.push(stakeStep)
    return baseSteps
  }, [approveStep, stakingType, lockTimeSelectStep, stakeStep])

  return (
    <>
      <StepLineModal
        isOpen={isConfirmationOpen}
        onDismiss={onFinish}
        onFinish={onFinish}
        header={<>Stake</>}
        steps={steps}
      />
      <CurrencyInputPanel
        value={value}
        onUserInput={setValue}
        onMax={() => {
          setValue(balance?.toExact() || '')
        }}
        currency={token}
        id="add-liquidity-input-tokena"
        showCommonBases
        showMaxButton
      />
      <ButtonError
        onClick={() => setIsConfirmationOpen(true)}
        disabled={isError || !value || parseFloat(value) === 0}
        error={isError}
      >
        {isError ? 'Insufficient balance' : 'Stake'}
      </ButtonError>
    </>
  )
}

export default StakeView
